import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../formatted/UseWindowsDimensions";
import { formatPercentage } from "../../shared/utils/utils";
import { EchartsZoomFrame } from "./echartsZoom";

/** labelFontSize: optional; when set (e.g. "1rem"), used for PDF export so labels are larger only in PDF */
/** centerDisplayValue: optional; when set, displayed in center instead of materialTotal (e.g. totalWeight for outlet chart) */
export const MaterialCountingPieChart = ({
  dataChart,
  labelChart,
  materialTotal,
  chartTotal,
  centerDisplayValue,
  colorChart,
  statistic,
  showMaterialTotal,
  radius,
  title,
  labelFontSize,
  centerLabelVariant = "material",
}) => {
  const { t, i18n } = useTranslation();
  const centerValue = centerDisplayValue ?? materialTotal;
  const materialLengt = materialTotal.toString().length;
  const { widthWindow } = useWindowDimensions();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(
    () => {
      if (!chartRef.current) return;

      if (!chartInstanceRef.current) {
        chartInstanceRef.current = echarts.init(chartRef.current);
      }
      const materialChart = chartInstanceRef.current;

      const wrapLabel = (label, maxLineLength = 12) => {
        if (!label) return "";
        const words = label.split(" ");
        let lines = [];
        let currentLine = "";

        for (const word of words) {
          if ((currentLine + word).length <= maxLineLength) {
            currentLine += (currentLine ? " " : "") + word;
          } else {
            lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines.join("\n");
      };

      const percentageDenominator =
        chartTotal != null ? chartTotal : materialTotal;

      const data = dataChart
        .map((value, index) => ({
          value: formatPercentage(value, percentageDenominator),
          name: labelChart[index],
          itemStyle: {
            color: colorChart[index],
          },
        }))
        .filter((_, index) => Number(dataChart[index]) > 0);

      const option = {
        tooltip: {
          trigger: "item",
          formatter: function (params) {
            return `${wrapLabel(params.name)} (${params.value}%)`;
          },
        },
        graphic: showMaterialTotal
          ? [
              {
                type: "group",
                left: "center",
                top: "center",
                children: [
                  {
                    type: "text",
                    left: "center",
                    top: "center",
                    style: {
                      text: centerValue,
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      textAlign: "center",
                    },
                  },
                  {
                    type: "text",
                    left: "center",
                    top: 15,
                    style: {
                      text:
                        title ??
                        (centerLabelVariant === "errors"
                          ? t("Errors detected")
                          : materialTotal > 1
                            ? t("Detected materials")
                            : t("Detected material")),
                      fontSize:
                        title === t("Value by material")
                          ? "1.5rem"
                          : labelFontSize || "0.8rem",
                      fontWeight: 400,
                      textAlign: "center",
                    },
                  },
                ],
              },
            ]
          : [],
        series: [
          {
            type: "pie",
            radius: radius
              ? radius
              : [statistic ? "27%" : "20%", statistic ? "70%" : "55%"],
            label: {
              formatter: function (params) {
                return `${wrapLabel(params.name)} (${params.value}%)`;
              },
              position: "outside",
              fontSize: labelFontSize || "0.8rem",
              // Match the "Reports" pie chart style (more readable in PDF capture).
              fontWeight: 400,
              overflow: "break",
            },
            labelLine: {
              length: 12,
              length2: 8,
            },
            data: data,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
          },
        ],
      };

      materialChart.setOption(option);

      // Gérer le resize uniquement pour les changements de taille de fenêtre réels
      const resizeFn = () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.resize();
        }
      };
      window.addEventListener("resize", resizeFn);

      return () => {
        window.removeEventListener("resize", resizeFn);
      };
    },
    // eslint-disable-next-line
    [
      centerLabelVariant,
      chartTotal,
      colorChart,
      dataChart,
      i18n.language,
      labelChart,
      labelFontSize,
      materialLengt,
      materialTotal,
      centerDisplayValue,
      centerValue,
      t,
      statistic,
      showMaterialTotal,
      radius,
      title,
    ],
  );

  // Nettoyer l'instance uniquement au démontage du composant
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <EchartsZoomFrame
      chartRef={chartRef}
      height="100%"
      width={statistic ? widthWindow / 2.3 : "100%"}
    />
  );
};
